import { State } from 'modules';

import orgFeatures from 'common/data/org-features';
import {
  AccessLevel,
  BoardPermissionLevel,
  MemberModel,
  MembershipModel,
  OrganizationFeature,
  TeamModel,
} from 'types';
import { getMe, getMemberById } from './members';
import { getMemberTypeFromMemberships } from './memberships';
import { getMyId } from './session';

export function isOrganizationOwned(state: State, org: TeamModel | string) {
  return (
    getTeamMembershipType(state, org, getMe(state) || '') === AccessLevel.Admin
  );
}

export function getTeamMembershipType(
  state: State,
  org: TeamModel | string,
  member: MemberModel | string,
) {
  const teamModel = typeof org === 'string' ? getTeamById(state, org) : org;
  const memberModel =
    typeof member === 'string' ? getMemberById(state, member)! : member;

  return getMemberTypeFromMemberships(
    state,
    teamModel.memberships,
    memberModel,
    teamModel.id,
  );
}

export function isMemberOfTeam(
  state: State,
  org: TeamModel | string,
  member: MemberModel | string,
) {
  const memberType = getTeamMembershipType(state, org, member);

  return (
    !!memberType &&
    [AccessLevel.Virtual, AccessLevel.Normal, AccessLevel.Admin].includes(
      memberType,
    )
  );
}

export function teamCanSetVisibility(
  state: State,
  org: TeamModel | string,
  vis: BoardPermissionLevel,
) {
  const orgModel: TeamModel =
    typeof org === 'string' ? getTeamById(state, org) : org;

  if (!orgModel) {
    return false;
  }

  const isRestrictVisEnabled = orgFeatures.isFeatureEnabled(
    OrganizationFeature.RestrictVis,
    orgModel.products,
  );

  if (!isRestrictVisEnabled) {
    return true;
  }

  const pref = orgModel.prefs && orgModel.prefs.boardVisibilityRestrict[vis];

  return (
    !pref ||
    pref === AccessLevel.Org ||
    (pref === AccessLevel.Admin && isOrganizationOwned(state, org))
  );
}

export interface OrgWithPermissions {
  allowedVis: BoardPermissionLevel[];
  model: TeamModel;
}

export function getOrgsWithPermissions(state: State): OrgWithPermissions[] {
  const permissions: BoardPermissionLevel[] = [
    AccessLevel.Private,
    AccessLevel.Org,
    AccessLevel.Public,
    AccessLevel.Enterprise,
  ];
  const orgsWithPermissions = getMyTeams(state).map((org: TeamModel) => ({
    allowedVis: permissions.filter((vis) =>
      teamCanSetVisibility(state, org, vis),
    ),
    model: org,
  }));

  return orgsWithPermissions;
}

export function getMyTeams(state: State) {
  const idMe = getMyId(state);

  if (!idMe) {
    return [];
  }

  return state.models.teams.filter(
    ({ memberships }: { memberships: MembershipModel[] }) => {
      return memberships.some(({ idMember }) => idMember === idMe);
    },
  );
}

const filterTeams = (
  teams: TeamModel[],
  filterFn: (team: TeamModel) => boolean,
) => {
  return teams.filter(filterFn)[0] || null;
};

const filterMyTeams = (
  state: State,
  filterFn: (team: TeamModel) => boolean,
) => {
  const teams = getMyTeams(state);

  if (teams.length === 0) {
    return null;
  }

  return filterTeams(teams, filterFn);
};

export function getTeamById(state: State, idTeam: string) {
  return filterTeams(state.models.teams, ({ id }) => id === idTeam);
}

export const getMyTeamByName = (state: State, teamName: string) =>
  filterMyTeams(state, ({ name }) => name === teamName);

export const getMyTeamById = (state: State, idTeam: string) =>
  filterMyTeams(state, ({ id }) => id === idTeam);

export const getMyTeamsByIds = (state: State, idTeams: string[] = []) =>
  idTeams.map((idTeam) => getMyTeamById(state, idTeam));



// WEBPACK FOOTER //
// ./app/gamma/src/selectors/teams.ts